/**
 * ChatView — message list + input for the AutoDieta IA chat.
 *
 * Supports message types:
 *   - text-only:   conversational AI response rendered via textContent (XSS-safe)
 *   - plan:        AI response with a MealPlan → rendered as MealPlanCard
 *   - foodLog:     food log confirmation (Fase 3)
 *   - evolution:   evolution point confirmation (Fase 3)
 *   - proposal:    re-plan proposal card (Fase 3 — propose-not-apply)
 *
 * Accessibility (CLAUDE.md §7): semantic HTML, keyboard navigation,
 * aria-live for status/errors, 44px touch target on send button,
 * Enter submits / Shift+Enter inserts newline.
 */

import { useCallback, useRef, useState } from "react";
import type { MealPlan, FoodLogEntry, EvolutionPoint, ReplanProposal } from "@autodieta/shared";
import { MealPlanCard } from "./MealPlanCard.js";
import { ProposalCard } from "../dashboard/ProposalCard.js";
import { CitationList } from "./CitationList.js";
import type { Citation } from "./CitationList.js";
import ReactMarkdown from "react-markdown";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/**
 * A chat message payload — plain text, plan, food log, evolution, proposal, or grounded.
 *
 * Phase 4 — kind:"grounded": AI response backed by external evidence (RAG/web).
 * The citations array is rendered by CitationList below the message text.
 * D-CITE: citations are structurally separate from agent opinion text.
 */
export type ChatMessageContent =
  | { kind: "text"; text: string }
  | { kind: "plan"; text: string; mealPlan: MealPlan }
  | { kind: "foodLog"; text: string; foodLogEntry: FoodLogEntry }
  | { kind: "evolution"; text: string; evolutionPoint: EvolutionPoint }
  | { kind: "proposal"; text: string; replanProposal: ReplanProposal }
  | { kind: "grounded"; text: string; citations: Citation[] };

/** A prior conversation turn for history forwarding (BUG-1 fix). */
export interface ConversationTurn {
  role: "user" | "assistant";
  content: string;
}

export interface ChatViewProps {
  /**
   * Called with the user message text and the prior conversation history.
   * Returns a ChatMessageContent describing the AI response.
   *
   * The `history` array contains all previous user/assistant turns (excluding
   * the current turn being sent). ChatPanel uses this to forward the full
   * conversation to the worker so the LLM has multi-turn context (BUG-1 fix).
   */
  sendMessage: (text: string, history: ConversationTurn[]) => Promise<ChatMessageContent>;
}

interface Message {
  id: string;
  role: "user" | "assistant";
  content: ChatMessageContent;
}

let idCounter = 0;
function nextId(): string {
  return String(++idCounter);
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function ChatView({ sendMessage }: ChatViewProps): JSX.Element {
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  const handleSend = useCallback(async () => {
    const text = input.trim();
    if (!text || loading) return;

    setInput("");
    setError(null);
    setLoading(true);

    const userContent: ChatMessageContent = { kind: "text", text };
    const userMsg: Message = { id: nextId(), role: "user", content: userContent };

    // Snapshot current messages BEFORE adding the new user turn,
    // so history contains only the prior turns (not this one).
    // BUG-1 fix: build ConversationTurn[] to forward to the worker.
    setMessages((prev) => {
      // Build history from the current messages (all turns so far).
      // We call sendMessage inside the state updater callback to capture
      // the exact prev array at dispatch time, avoiding a stale closure.
      // However, setMessages cannot be async — we derive history from
      // the current `messages` state captured in the closure below.
      return [...prev, userMsg];
    });

    try {
      // BUG-1 fix: derive history from the messages state at call time.
      // `messages` here is the value from the last render — before setMessages fires.
      // This is intentional: we want all PREVIOUS turns, not the one being sent now.
      const history: ConversationTurn[] = messages.map((m) => ({
        role: m.role,
        content: m.content.text,
      }));

      const reply = await sendMessage(text, history);
      const aiMsg: Message = { id: nextId(), role: "assistant", content: reply };
      setMessages((prev) => [...prev, aiMsg]);
    } catch {
      setError("No se pudo obtener una respuesta. Intentá de nuevo.");
    } finally {
      setLoading(false);
      inputRef.current?.focus();
    }
  }, [input, loading, messages, sendMessage]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      void handleSend();
    }
    // Shift+Enter: native textarea behavior inserts a newline
  };

  return (
    <section aria-label="chat" className="flex h-full flex-col gap-4">
      {/* Message list */}
      <div
        aria-live="polite"
        aria-label="mensajes"
        className="flex flex-1 flex-col gap-3 overflow-y-auto"
      >
        {messages.length === 0 ? (
          <p className="py-16 text-center text-sm text-zinc-500">
            Empezá a chatear con AutoDieta IA.
          </p>
        ) : null}

        {messages.map((msg) => (
          <MessageBubble key={msg.id} message={msg} />
        ))}

        {/* Loading indicator */}
        {loading ? (
          <p
            role="status"
            aria-live="polite"
            aria-label="esperando respuesta"
            className="flex items-center gap-2 text-sm text-zinc-400"
          >
            <span
              aria-hidden="true"
              className="h-3 w-3 animate-spin rounded-full border-2 border-zinc-600 border-t-blue-400"
            />
            AutoDieta IA está respondiendo…
          </p>
        ) : null}
      </div>

      {/* Error display */}
      {error ? (
        <p
          role="alert"
          aria-live="assertive"
          className="rounded-lg border border-red-900/50 bg-red-950/20 px-4 py-2 text-sm text-red-400"
        >
          {error}
        </p>
      ) : null}

      {/* Input area */}
      <form
        className="flex items-end gap-2"
        onSubmit={(e) => {
          e.preventDefault();
          void handleSend();
        }}
      >
        <label className="flex-1">
          <span className="sr-only">Mensaje</span>
          <textarea
            ref={inputRef}
            aria-label="Mensaje"
            rows={2}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            disabled={loading}
            placeholder="Escribí tu mensaje… (Enter para enviar, Shift+Enter para nueva línea)"
            className="w-full resize-none rounded-lg border border-zinc-700 bg-zinc-900 px-4 py-3 text-sm text-zinc-100 placeholder-zinc-500 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-400 disabled:opacity-50"
          />
        </label>
        <button
          type="submit"
          aria-label="Enviar mensaje"
          disabled={loading || input.trim().length === 0}
          className="min-h-[44px] shrink-0 rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-blue-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-400 disabled:opacity-50"
        >
          Enviar
        </button>
      </form>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Message bubble
// ---------------------------------------------------------------------------

interface MessageBubbleProps {
  message: Message;
}

/**
 * Renders AI-generated markdown safely as React nodes.
 *
 * Uses react-markdown which produces React elements — never innerHTML.
 * Complies with CLAUDE.md §security: no dangerouslySetInnerHTML with untrusted content.
 *
 * Supported: bold, italic, lists (ordered/unordered), inline code, blockquote,
 * tables, and line breaks. Links are rendered as plain text (no href injection).
 *
 * BUG-3 fix: replaces raw {text} rendering that showed markdown syntax as-is.
 */
function AssistantMarkdown({ text }: { text: string }): JSX.Element {
  return (
    <ReactMarkdown
      components={{
        // Headings: scale down to match chat bubble size
        h1: ({ children }) => (
          <p className="mb-1 font-semibold text-zinc-100">{children}</p>
        ),
        h2: ({ children }) => (
          <p className="mb-1 font-semibold text-zinc-200">{children}</p>
        ),
        h3: ({ children }) => (
          <p className="mb-1 font-medium text-zinc-200">{children}</p>
        ),
        // Paragraphs
        p: ({ children }) => (
          <p className="mb-2 last:mb-0 leading-relaxed">{children}</p>
        ),
        // Unordered lists (e.g., missing profile fields as bullet list)
        ul: ({ children }) => (
          <ul className="mb-2 list-disc pl-5 leading-relaxed">{children}</ul>
        ),
        // Ordered lists
        ol: ({ children }) => (
          <ol className="mb-2 list-decimal pl-5 leading-relaxed">{children}</ol>
        ),
        li: ({ children }) => <li className="mb-0.5">{children}</li>,
        // Bold and italic
        strong: ({ children }) => (
          <strong className="font-semibold text-zinc-50">{children}</strong>
        ),
        em: ({ children }) => <em className="italic">{children}</em>,
        // Inline code (e.g., field names)
        code: ({ children }) => (
          <code className="rounded bg-zinc-800 px-1 py-0.5 font-mono text-xs text-zinc-300">
            {children}
          </code>
        ),
        // Tables (for structured plan previews)
        table: ({ children }) => (
          <div className="mb-2 overflow-x-auto">
            <table className="min-w-full border-collapse text-xs">{children}</table>
          </div>
        ),
        th: ({ children }) => (
          <th className="border border-zinc-700 bg-zinc-800 px-2 py-1 text-left font-medium text-zinc-200">
            {children}
          </th>
        ),
        td: ({ children }) => (
          <td className="border border-zinc-700 px-2 py-1 text-zinc-300">{children}</td>
        ),
        // Links: render as plain text to prevent href injection from LLM output
        a: ({ children }) => (
          <span className="text-blue-400 underline">{children}</span>
        ),
        // Blockquote
        blockquote: ({ children }) => (
          <blockquote className="mb-2 border-l-2 border-zinc-600 pl-3 text-zinc-400">
            {children}
          </blockquote>
        ),
        // Horizontal rule
        hr: () => <hr className="my-2 border-zinc-700" />,
      }}
    >
      {text}
    </ReactMarkdown>
  );
}

function MessageBubble({ message }: MessageBubbleProps): JSX.Element {
  const isUser = message.role === "user";

  // User messages are always plain text (user controls their own content)
  if (isUser) {
    return (
      <div className="flex justify-end">
        <div className="max-w-[80%] rounded-2xl bg-blue-600 px-4 py-2.5 text-sm leading-relaxed text-white">
          {message.content.text}
        </div>
      </div>
    );
  }

  // Assistant text: render with markdown (BUG-3 fix).
  // Uses AssistantMarkdown which produces React nodes, never innerHTML.
  if (message.content.kind === "text") {
    const text = message.content.text;
    return (
      <div className="flex justify-start">
        <div className="max-w-[80%] rounded-2xl border border-zinc-800 bg-zinc-900 px-4 py-2.5 text-sm leading-relaxed text-zinc-100">
          <AssistantMarkdown text={text} />
        </div>
      </div>
    );
  }

  // Assistant message with a meal plan
  if (message.content.kind === "plan") {
    const { mealPlan, text } = message.content;
    return (
      <div className="flex justify-start">
        <div className="w-full max-w-2xl">
          {text ? (
            <div className="mb-3 rounded-2xl border border-zinc-800 bg-zinc-900 px-4 py-2.5 text-sm leading-relaxed text-zinc-100">
              <AssistantMarkdown text={text} />
            </div>
          ) : null}
          <MealPlanCard mealPlan={mealPlan} />
        </div>
      </div>
    );
  }

  // Fase 3: food log confirmation
  if (message.content.kind === "foodLog") {
    const { foodLogEntry, text } = message.content;
    return (
      <div className="flex justify-start">
        <div className="w-full max-w-2xl">
          <div className="rounded-2xl border border-zinc-800 bg-zinc-900 px-4 py-2.5 text-sm leading-relaxed text-zinc-100">
            {text ? <AssistantMarkdown text={text} /> : "Alimento registrado."}
          </div>
          <FoodLogConfirmation entry={foodLogEntry} />
        </div>
      </div>
    );
  }

  // Fase 3: evolution point confirmation
  if (message.content.kind === "evolution") {
    const { evolutionPoint, text } = message.content;
    return (
      <div className="flex justify-start">
        <div className="w-full max-w-2xl">
          <div className="rounded-2xl border border-zinc-800 bg-zinc-900 px-4 py-2.5 text-sm leading-relaxed text-zinc-100">
            {text ? <AssistantMarkdown text={text} /> : "Peso registrado."}
          </div>
          <EvolutionConfirmation point={evolutionPoint} />
        </div>
      </div>
    );
  }

  // Phase 4: grounded response — AI reply backed by external evidence (RAG/web).
  // AssistantMarkdown produces React nodes — never innerHTML. Complies with CLAUDE.md §security.
  if (message.content.kind === "grounded") {
    const { text, citations } = message.content;
    return (
      <div className="flex justify-start">
        <div className="w-full max-w-2xl">
          {text ? (
            <div className="mb-2 rounded-2xl border border-zinc-800 bg-zinc-900 px-4 py-2.5 text-sm leading-relaxed text-zinc-100">
              <AssistantMarkdown text={text} />
            </div>
          ) : null}
          {citations.length > 0 ? <CitationList citations={citations} /> : null}
        </div>
      </div>
    );
  }

  // Fase 3: re-plan proposal (propose-not-apply)
  const { replanProposal, text } = message.content;
  return (
    <div className="flex justify-start">
      <div className="w-full max-w-2xl">
        {text ? (
          <div className="mb-3 rounded-2xl border border-zinc-800 bg-zinc-900 px-4 py-2.5 text-sm leading-relaxed text-zinc-100">
            <AssistantMarkdown text={text} />
          </div>
        ) : null}
        <ProposalCard
          proposal={replanProposal}
          onConfirm={async (_id) => {
            // TODO: wire to worker /confirm-proposal endpoint (follow-up task)
          }}
          onReject={async (_id) => {
            // TODO: wire to worker /reject-proposal endpoint (follow-up task)
          }}
        />
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// FoodLogConfirmation
// ---------------------------------------------------------------------------

import type { FoodLogEntry as FoodLogEntryType } from "@autodieta/shared";

function FoodLogConfirmation({ entry }: { entry: FoodLogEntryType }): JSX.Element {
  return (
    <div
      aria-live="polite"
      className="mt-2 rounded-xl border border-zinc-700 bg-zinc-900 px-4 py-3 text-sm"
    >
      <p className="mb-2 font-medium text-zinc-200">Alimento registrado</p>
      <ul className="flex flex-col gap-1 text-xs text-zinc-400">
        {entry.items.map((item) => (
          <li key={item.foodName}>
            {item.foodName} — {item.grams} g ({Math.round(item.kcal)} kcal)
          </li>
        ))}
      </ul>
      <p className="mt-2 text-xs text-zinc-500">
        Total: {Math.round(entry.totals.kcal)} kcal |{" "}
        P: {Math.round(entry.totals.proteinG)} g |{" "}
        C: {Math.round(entry.totals.carbG)} g |{" "}
        G: {Math.round(entry.totals.fatG)} g
      </p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// EvolutionConfirmation
// ---------------------------------------------------------------------------

import type { EvolutionPoint as EvolutionPointType } from "@autodieta/shared";

function EvolutionConfirmation({ point }: { point: EvolutionPointType }): JSX.Element {
  return (
    <div
      aria-live="polite"
      className="mt-2 rounded-xl border border-zinc-700 bg-zinc-900 px-4 py-3 text-sm"
    >
      <p className="mb-1 font-medium text-zinc-200">Peso registrado</p>
      {point.weightKg != null ? (
        <p className="text-xs text-zinc-400">
          Peso: <strong className="text-zinc-200">{point.weightKg} kg</strong>
        </p>
      ) : null}
      {point.bodyFatPct != null ? (
        <p className="text-xs text-zinc-400">
          Grasa corporal: <strong className="text-zinc-200">{point.bodyFatPct}%</strong>
        </p>
      ) : null}
    </div>
  );
}
